import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import RetroLinkButton from "../components/RetroLinkButton";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" }); // Clear errors on input change
    setSuccessMessage(""); // Clear the success message on input change
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message cannot be empty.";
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSuccessMessage("");
      return;
    }

    setIsSubmitting(true);

    // Send email via your backend
    fetch(`${process.env.REACT_APP_API_URL}/send-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to send message");
        }
        return response.json();
      })
      .then((data) => {
        console.log("SUCCESS!", data);
        setSuccessMessage("Message sent successfully!");
        setFormData({ name: "", email: "", message: "" });
      })
      .catch((error) => {
        console.error("FAILED...", error);
        setErrors({ submit: "Failed to send message. Please try again later." });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <PageLayout>
      <div className="text-[1.18rem] lg:text-[1.02rem] xl:text-[1.18rem] font-lusitana">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
          <h1 className="text-[3.6rem] md:text-[4rem] lg:text-[3.2rem] xl:text-[4rem] font-bold font-garamond">
            Let's Connect
          </h1>
          <div className="flex space-x-2 mt-[-0.3rem] mb-[0.95rem] md:mb-0 md:mt-0">
            <RetroLinkButton
              href="https://linkedin.com/in/aaronzzhu"
              src="/contact-logos/linkedin-logo.png"
              alt="LinkedIn"
            />
            <RetroLinkButton
              href="https://github.com/zhucs"
              src="/contact-logos/github-logo.png"
              alt="GitHub"
            />
            <RetroLinkButton
              href="mailto:aaron.zhu@columbia.edu"
              src="/contact-logos/email-logo.png"
              alt="Email"
            />
          </div>
        </div>

        {/* Contact Message */}
        <div className="leading-[1.55rem] mt-[-0.4rem] mb-[0.2rem]">
          Reach out to me by email at{" "}
          <a
            href="mailto:aaron.zhu@columbia.edu"
            className="text-blue-800 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            aaron.zhu@columbia.edu
          </a>{" "}
          or leave me a message with the form below!
        </div>

        {/* Message Form */}
        <div className="bg-[#c0c0c0] border-[0.12rem] border-t-[#f5f5f5] border-l-[#f5f5f5] border-b-black border-r-black shadow-inner p-3 mt-2 mb-6">
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Name Field */}
            <div className="flex flex-col">
              <label htmlFor="name" className="font-bold mb-1">
                Name:<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className={`bg-gray-100 w-full h-[2.5rem] px-2 border-[0.13rem] border-t-[#595659] border-l-[#595659] border-b-white border-r-white shadow-inner ${
                  errors.name ? "border-red-600" : ""
                }`}
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <span className="text-red-600 text-sm mt-1">{errors.name}</span>
              )}
            </div>

            {/* Email Field */}
            <div className="flex flex-col">
              <label htmlFor="email" className="font-bold mb-1">
                Email:<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="text"
                id="email"
                name="email"
                className={`bg-gray-100 w-full h-[2.5rem] px-2 border-[0.13rem] border-t-[#595659] border-l-[#595659] border-b-white border-r-white shadow-inner ${
                  errors.email ? "border-red-600" : ""
                }`}
                placeholder="Your email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <span className="text-red-600 text-sm mt-1">{errors.email}</span>
              )}
            </div>

            {/* Message Field */}
            <div className="flex flex-col">
              <label htmlFor="message" className="font-bold mb-1">
                Message:<span className="text-red-600 ml-1">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows="4"
                className={`bg-gray-100 w-full px-2 py-1 border-[0.13rem] border-t-[#595659] border-l-[#595659] border-b-white border-r-white shadow-inner ${
                  errors.message ? "border-red-600" : ""
                }`}
                placeholder="Your message"
                value={formData.message}
                onChange={handleChange}
              ></textarea>
              {errors.message && (
                <span className="text-red-600 text-sm mt-1">{errors.message}</span>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#c0c0c0] w-[8rem] h-[2.8rem] flex items-center justify-center border-[0.13rem] border-t-white border-l-white border-b-black border-r-black shadow-inner font-bold"
              >
                {isSubmitting ? "Sending..." : "Send"}
              </button>
              {successMessage && (
                <span className="text-green-600 font-bold">{successMessage}</span>
              )}
            </div>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default Contact;
